import debounce from 'lodash/debounce'
import React from 'react'
import { FC, useEffect, useState } from 'react'
import { LuCircleAlert } from 'react-icons/lu'
import AsyncCreatableSelect from 'react-select/async-creatable'

import { Field, FieldLabel } from '@/components/ui/field'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useAppDispatch from '@/hooks/useAppDispatch'
import useAppSelector from '@/hooks/useAppSelector'
import {
  DOM_IDS,
  REFERRAL_IMPORT_DIRECTION,
  SERVICE_AREA_ID,
} from '@/lib/constants'
import { isValidEmail } from '@/lib/helpers/emailAddressHelpers'
import referralService from '@/lib/services/referralService'
import { cn } from '@/lib/utils'
import {
  selectLogHistoricalReferralState,
  updateLogData,
} from '@/redux/slices/logHistoricalReferral'

interface Props {
  className?: string
  disabled?: boolean
  initialInputValue?: string
}

const SelectCompanyOrAdviser: FC<Props> = ({
  className,
  disabled = false,
  initialInputValue,
}) => {
  const referralLogHistoricalState = useAppSelector(
    selectLogHistoricalReferralState,
  )
  const dispatch = useAppDispatch()

  const {
    loadOptions,
    isLoading,
    isValidNewOption,
    getNewOptionData,
    formatCreateLabel,
    formatOptionLabel,
    findFirmsOrIndividuals,
  } = useSelectCompanyOrAdvisor()

  const [selectedOption, setSelectedOption] = useState<SelectOption | null>(
    null,
  )

  // @todo: avoid useEffect
  useEffect(() => {
    const seed = initialInputValue?.trim()

    if (!seed) {
      return
    }
    findFirmsOrIndividuals(seed).then((options) => {
      const firstApproved = options.find((o) => !o.isDisabled)

      if (firstApproved) {
        selectOption(firstApproved)
        return
      }

      if (isValidEmail(seed)) {
        const offRqOption = getNewOptionData(seed)
        selectOption(offRqOption)
      }
    })
  }, [initialInputValue]) // eslint-disable-line react-hooks/exhaustive-deps -- sync from parent seed; avoid rebind loops

  function selectOption(option: SelectOption | null) {
    if (!option) {
      setSelectedOption(null)
      dispatch(
        updateLogData({
          otherCompany: null,
          otherCompanyUser: null,
          advisorEmail: '',
          advisorEmailValid: false,
          newServiceAreaId: null,
          serviceAreaId: -1,
        }),
      )
      return
    }

    setSelectedOption(option)
    if (option.advisorId && option.companyId) {
      dispatch(
        updateLogData({
          otherCompany: {
            id: option.companyId,
            presentationName: option.subLabel || '',
          },
          otherCompanyUser: {
            id: option.advisorId,
            email: option.label,
            firstName: option.label,
            lastName: option.label,
          },
          advisorEmail: '',
          advisorEmailValid: false,
          newServiceAreaId: null,
          serviceAreaId: -1,
        }),
      )
    } else if (option.companyId) {
      dispatch(
        updateLogData({
          otherCompany: {
            id: option.companyId,
            presentationName: option.label,
          },
          otherCompanyUser: null,
          advisorEmail: '',
          advisorEmailValid: false,
          newServiceAreaId: null,
          serviceAreaId: -1,
        }),
      )
    } else if (option.newAdvisorEmail) {
      const updateService =
        referralLogHistoricalState.logData.referralDirection ===
        REFERRAL_IMPORT_DIRECTION.TO
          ? {
              newServiceAreaId: null,
              serviceAreaId: SERVICE_AREA_ID.generalEnquiry,
            }
          : {}
      dispatch(
        updateLogData({
          otherCompany: null,
          otherCompanyUser: null,
          advisorEmail: option.newAdvisorEmail,
          advisorEmailValid: true,
          ...updateService,
        }),
      )
    }
  }

  return (
    <div className={cn('relative', className)}>
      <AsyncCreatableSelect
        isDisabled={disabled}
        cacheOptions
        defaultOptions
        placeholder="Search by company or adviser name..."
        loadOptions={loadOptions}
        menuPortalTarget={document.getElementById(DOM_IDS.reactSelectPortal)}
        menuPosition="fixed"
        isLoading={isLoading}
        value={selectedOption}
        onChange={(newValue) => selectOption(newValue as SelectOption | null)}
        isValidNewOption={isValidNewOption}
        getNewOptionData={getNewOptionData}
        formatCreateLabel={formatCreateLabel}
        formatOptionLabel={formatOptionLabel}
        styles={{
          singleValue: (base, state) => ({
            ...base,
            opacity: state.selectProps.menuIsOpen ? 0.1 : 1,
          }),
        }}
      />

      {referralLogHistoricalState.logData.advisorEmail && (
        <Field
          orientation="horizontal"
          className="my-4 flex items-center gap-2"
        >
          <Switch
            checked={referralLogHistoricalState.logData.inviteAdvisor}
            onCheckedChange={() =>
              dispatch(
                updateLogData({
                  inviteAdvisor:
                    !referralLogHistoricalState.logData.inviteAdvisor,
                }),
              )
            }
            disabled={disabled}
            className="inline-block"
            id="sendInviteEmail"
          />

          <Tooltip>
            <TooltipTrigger asChild>
              <FieldLabel htmlFor="sendInviteEmail">
                Send invite mail to “
                {referralLogHistoricalState.logData.advisorEmail}”{' '}
                <LuCircleAlert className="inline size-3" />
              </FieldLabel>
            </TooltipTrigger>

            <TooltipContent>
              {referralLogHistoricalState.logData.inviteAdvisor ? (
                <>
                  “{referralLogHistoricalState.logData.advisorEmail}” will be
                  invited to join you on RQ. It’s free for them to sign up and
                  work with you on RQ.
                </>
              ) : (
                <>
                  This referral will be logged against “
                  {referralLogHistoricalState.logData.advisorEmail}”, but the
                  adviser will not be invited. You can invite them to join you
                  later.
                </>
              )}
            </TooltipContent>
          </Tooltip>
        </Field>
      )}

      <div className="my-4 hidden">
        {referralLogHistoricalState.logData.otherCompanyUser && (
          <div>
            Selected adviser:{' '}
            {referralLogHistoricalState.logData.otherCompanyUser.firstName}
          </div>
        )}

        {referralLogHistoricalState.logData.otherCompany && (
          <div>
            Selected company:{' '}
            {referralLogHistoricalState.logData.otherCompany.presentationName}
          </div>
        )}

        {referralLogHistoricalState.logData.advisorEmail && (
          <div>
            New adviser: {referralLogHistoricalState.logData.advisorEmail}
          </div>
        )}
      </div>
    </div>
  )
}

interface SelectOption {
  label: string
  value: string
  isDisabled: boolean
  subLabel?: string
  companyId?: number
  advisorId?: number
  newAdvisorEmail?: string
}

function useSelectCompanyOrAdvisor() {
  const [isLoading, setIsLoading] = useState(false)

  function isValidNewOption(inputValue: string) {
    return isValidEmail(inputValue)
  }

  function getNewOptionData(inputValue: string): SelectOption {
    return {
      label: `${inputValue}`,
      value: `newAdvisor-${inputValue}`,
      subLabel: `Log a referral to someone not on RQ`,
      newAdvisorEmail: inputValue,
      isDisabled: false,
    }
  }

  function formatCreateLabel(inputValue: string): string {
    return `Refer to someone not yet on RQ "${inputValue}"`
  }

  function formatOptionLabel(option: SelectOption) {
    return (
      <div
        className={cn({ 'text-disabled': option.isDisabled })}
        onClick={(event) => {
          if (option.isDisabled) {
            event.preventDefault()
            event.stopPropagation()
          }
        }}
      >
        {option.label}
        {option.subLabel && (
          <span className="xxs block">{option.subLabel}</span>
        )}
      </div>
    )
  }

  async function findFirmsOrIndividuals(search: string) {
    // The companies will show advisors, so there will be multiple companies with different advisors on the list
    // Advisors are also loaded separately for filtering
    // "search" will filter companies names or advisors
    const options: SelectOption[] = []

    const [resultsIndividuals, resultsFirms] = await Promise.all([
      // a little trick to not load advisors when empty string (they will be all loaded for companies)
      referralService.getReferralIndividuals({
        name: search,
        companyId: search ? undefined : -1,
      }),
      referralService.getReferralFirms({ name: search }),
    ])

    if (resultsFirms['hydra:member'].length > 0) {
      // load advisors for filtered companies
      const advisorsByCompanies = await Promise.all(
        resultsFirms['hydra:member'].map((result) =>
          referralService.getReferralIndividuals({
            name: '',
            companyId: result.company.id,
          }),
        ),
      )

      // create options with company name as the main label and advisor as sub label
      advisorsByCompanies.forEach((advisors) => {
        advisors['hydra:member'].forEach((result) =>
          options.push({
            value: `companyUser-${result.company.id}-${result.user.id}`,
            label: result.company.presentationName,
            subLabel: result.user.fullName,
            companyId: result.company.id,
            advisorId: result.user.id,
            isDisabled: !result.isPanelApproved,
          }),
        )
      })
    }

    // create options for any advisor filtered by search query (main label advisor name, sub label company nane)
    resultsIndividuals['hydra:member'].forEach((result) =>
      options.push({
        value: `companyUser-${result.company.id}-${result.user.id}`,
        label: result.user.fullName,
        subLabel: result.company.presentationName,
        companyId: result.company.id,
        advisorId: result.user.id,
        isDisabled: !result.isPanelApproved,
      }),
    )

    return options
  }

  function loadOptions(
    search: string,
    callback: (options: SelectOption[]) => void,
  ) {
    setIsLoading(true)
    findFirmsOrIndividuals(search).then((options) => {
      callback(options)
      setIsLoading(false)
    })
  }

  const debouncedLoadOptions = debounce(loadOptions, 300)

  return {
    loadOptions: debouncedLoadOptions,
    findFirmsOrIndividuals,
    isLoading,
    isValidNewOption,
    getNewOptionData,
    formatCreateLabel,
    formatOptionLabel,
  }
}

export default SelectCompanyOrAdviser
